import React, { useState, useEffect } from 'react';
import { inventoryAPI } from '../services/api';

const StockAdjustmentModal = ({ product, onSubmit, onClose }) => {
    const [adjustmentType, setAdjustmentType] = useState('entrada'); // entrada, salida, ajuste
    const [quantity, setQuantity] = useState('');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');

    const [movements, setMovements] = useState([]);
    const [loadingMovements, setLoadingMovements] = useState(false);

    useEffect(() => {
        if (!product?.id) return;
        setLoadingMovements(true);
        inventoryAPI.getMovements({ product_id: product.id, limit: 10 })
            .then(res => setMovements(res.data.data.movements || []))
            .catch(() => setMovements([]))
            .finally(() => setLoadingMovements(false));
    }, [product?.id]);

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (quantity === '' || parseFloat(quantity) <= 0 && adjustmentType !== 'ajuste') {
            alert('La cantidad a ajustar debe ser mayor a 0');
            return;
        }
        if (adjustmentType === 'ajuste' && parseFloat(quantity) < 0) {
            alert('El nuevo stock no puede ser negativo');
            return;
        }
        
        if (!reason.trim()) {
            alert('Debes especificar una razón');
            return;
        }

        onSubmit({
            type: adjustmentType,
            quantity: parseFloat(quantity),
            reason: reason.trim(),
            notes: notes.trim() || null
        });
    };

    const getNewStock = () => {
        const qty = parseFloat(quantity) || 0;
        if (adjustmentType === 'entrada') return parseFloat(product.stock) + qty;
        if (adjustmentType === 'salida') return Math.max(0, parseFloat(product.stock) - qty);
        return qty; // ajuste directo
    };

    const formatStock = (val) => {
        if (val === undefined || val === null || isNaN(val)) return 0;
        return product.unit === 'kg' ? parseFloat(val).toFixed(2) : Math.floor(val);
    };

    return (
        <div style={{ padding: '20px' }}>
            {/* Info del producto */}
            <div style={{
                background: 'var(--bg-input)',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px'
            }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                        <strong>Código:</strong> {product.code}
                    </div>
                    <div>
                        <strong>Nombre:</strong> {product.name}
                    </div>
                    <div>
                        <strong>Stock Actual:</strong> 
                        <span style={{ 
                            color: product.stock <= product.min_stock ? '#e74c3c' : '#27ae60',
                            fontWeight: 'bold',
                            marginLeft: '8px',
                            fontSize: '18px'
                        }}>
                            {formatStock(product.stock)}
                        </span>
                    </div>
                    <div>
                        <strong>Stock Mínimo:</strong> {formatStock(product.min_stock)}
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Tipo de ajuste */}
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Tipo de Ajuste
                    </label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            type="button"
                            onClick={() => setAdjustmentType('entrada')}
                            style={{
                                flex: 1,
                                padding: '12px',
                                border: adjustmentType === 'entrada' ? '2px solid #27ae60' : '1px solid #ddd',
                                background: adjustmentType === 'entrada' ? '#d4edda' : 'var(--bg-card)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: adjustmentType === 'entrada' ? 'bold' : 'normal'
                            }}
                        >
                            📈 Entrada<br/>
                            <small style={{ color: '#7f8c8d' }}>Agregar stock</small>
                        </button>
                        <button
                            type="button"
                            onClick={() => setAdjustmentType('salida')}
                            style={{
                                flex: 1,
                                padding: '12px',
                                border: adjustmentType === 'salida' ? '2px solid #e74c3c' : '1px solid #ddd',
                                background: adjustmentType === 'salida' ? '#f8d7da' : 'var(--bg-card)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: adjustmentType === 'salida' ? 'bold' : 'normal'
                            }}
                        >
                            📉 Salida<br/>
                            <small style={{ color: '#7f8c8d' }}>Reducir stock</small>
                        </button>
                        <button
                            type="button"
                            onClick={() => setAdjustmentType('ajuste')}
                            style={{
                                flex: 1,
                                padding: '12px',
                                border: adjustmentType === 'ajuste' ? '2px solid #3498db' : '1px solid #ddd',
                                background: adjustmentType === 'ajuste' ? '#d6eaf8' : 'var(--bg-card)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: adjustmentType === 'ajuste' ? 'bold' : 'normal'
                            }}
                        >
                            🔄 Ajuste<br/>
                            <small style={{ color: '#7f8c8d' }}>Establecer valor</small>
                        </button>
                    </div>
                </div>

                {/* Cantidad */}
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        {adjustmentType === 'ajuste' ? 'Nuevo Stock' : 'Cantidad'}
                    </label>
                    <input
                        type="number"
                        min="0"
                        step={product.unit === 'kg' ? '0.01' : '1'}
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        placeholder={adjustmentType === 'ajuste' ? 'Ej: 100' : 'Ej: 50'}
                        style={{
                            width: '100%',
                            padding: '12px',
                            fontSize: '16px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                        required
                    />
                </div>

                {/* Preview del nuevo stock */}
                {quantity && (
                    <div style={{
                        background: 'var(--bg-input)',
                        padding: '12px',
                        borderRadius: '4px',
                        marginBottom: '15px',
                        textAlign: 'center'
                    }}>
                        <strong>Nuevo Stock:</strong> 
                        <span style={{ 
                            fontSize: '24px', 
                            fontWeight: 'bold',
                            marginLeft: '10px',
                            color: getNewStock() <= product.min_stock ? '#e74c3c' : '#27ae60'
                        }}>
                            {formatStock(product.stock)} → {formatStock(getNewStock())}
                        </span>
                    </div>
                )}

                {/* Razón */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Razón del Ajuste
                    </label>
                    <select
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px',
                            fontSize: '14px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            marginBottom: '10px'
                        }}
                        required
                    >
                        <option value="">Seleccionar razón...</option>
                        <optgroup label="Entrada">
                            <option value="Compra a proveedor">Compra a proveedor</option>
                            <option value="Devolución de cliente">Devolución de cliente</option>
                            <option value="Producción interna">Producción interna</option>
                            <option value="Ajuste por inventario">Ajuste por inventario</option>
                        </optgroup>
                        <optgroup label="Salida">
                            <option value="Venta">Venta</option>
                            <option value="Producto dañado">Producto dañado</option>
                            <option value="Producto vencido">Producto vencido</option>
                            <option value="Merma">Merma</option>
                            <option value="Donación">Donación</option>
                        </optgroup>
                        <optgroup label="Ajuste">
                            <option value="Corrección de inventario">Corrección de inventario</option>
                            <option value="Conteo físico">Conteo físico</option>
                        </optgroup>
                        <option value="Otro">Otro (especificar en notas)</option>
                    </select>
                    
                    <textarea
                        placeholder="Notas adicionales (opcional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px',
                            fontSize: '14px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            minHeight: '60px',
                            resize: 'vertical'
                        }}
                    />
                </div>

                {/* Botones */}
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        type="submit"
                        className="btn btn-success"
                        style={{ flex: 1, padding: '12px', fontSize: '16px' }}
                    >
                        ✅ Confirmar Ajuste
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-secondary"
                        style={{ flex: 1, padding: '12px', fontSize: '16px' }}
                    >
                        ❌ Cancelar
                    </button>
                </div>
            </form>

            {/* Historial de movimientos */}
            <div style={{ marginTop: 20, borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
                <h4 style={{ marginBottom: 10, color: 'var(--text-primary)' }}>📋 Últimos movimientos</h4>
                {loadingMovements ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Cargando...</p>
                ) : movements.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Sin movimientos registrados.</p>
                ) : (
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
                                <th style={{ paddingBottom: 6 }}>Fecha</th>
                                <th>Tipo</th>
                                <th>Cant.</th>
                                <th>Antes</th>
                                <th>Después</th>
                                <th>Razón</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movements.map((m, i) => (
                                <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}>
                                    <td style={{ padding: '5px 0' }}>
                                        {new Date(m.movement_date).toLocaleDateString('es-PE')}
                                    </td>
                                    <td>{m.movement_type === 'entrada' ? '📈' : m.movement_type === 'salida' ? '📉' : '🔄'} {m.movement_type}</td>
                                    <td>{parseFloat(m.quantity)}</td>
                                    <td>{parseFloat(m.previous_stock)}</td>
                                    <td>{parseFloat(m.new_stock)}</td>
                                    <td style={{ color: 'var(--text-secondary)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {[m.reason, m.notes].filter(Boolean).join(' · ') || '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default StockAdjustmentModal;